import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";

import { AWSLanguage, Subtitle } from "chq";
import { useProfile } from "app/context";

import { isSubtitleText, loadSubtitle, modificationsForAllSubtitles, removeSubtitlesElements, updateSubtitleStyles } from "./utils";
import SubtitlesContainer from "./SubtitlesContainer";
import SubtitleHeader from "./SubtitleHeader";
import AnimationScreen from "../../animation/AnimationScreen";
import { Context } from "../../animation/AnimationTypes";
import { CreatomateContext } from "../../../contexts/VideoStudioContext";

interface SubtitlesProps {
  preview: Preview;
}

const Subtitles: React.FC<SubtitlesProps> = ({ preview }) => {
  const { data, handleUpdateData, getActiveElement } = useContext(CreatomateContext);
  const { burnSubtitles, subtitlesOn, subtitles } = data;
  const activeElement = getActiveElement(preview) as ElementState;
  const { elements } = preview.state || { elements: [] };
  const subtitleElements = useMemo(() => elements.filter(
    element => isSubtitleText(element.source.name)), [elements]
  );
  const { profile } = useProfile();
  const { organization } = profile;
  const [error, setError] = useState<string | undefined>();
  const [changingLanguage, setChangingLanguage] = useState(false);
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const [showStyles, setShowStyles] = useState(false);
  const activeLanguage = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => subtitles.find(subtitle => subtitle.active), [changingLanguage, subtitles.length]
  );
  const [showAnimations, setShowAnimations] = useState(false);
  const isInitialRender = useRef(true);
  const isUpdating = useRef(false); // Track if updateSubtitles is running

  // set first subtitle element as active if no subtitle is active
  useEffect(
    () => {
      if (!isInitialRender.current) {
        const subtitle = subtitles.find(sub => sub.default);
        updateSubtitleStyles(
          burnSubtitles, subtitleElements, preview, organization.name, subtitle?.language
        );
      } else {
        isInitialRender.current = false;
      }

      const subtitleElement = subtitleElements[0];
      if (subtitleElement && (activeElement?.source?.type !== "text" || !isSubtitleText(activeElement?.source?.name))) {
        preview.setActiveElements([subtitleElement.source.id]);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [burnSubtitles]
  );

  useEffect(
    () => {
      const updateSubtitles = async (currentSubElement: ElementState) => {
        isUpdating.current = true;
        await preview.applyModifications({
          ...modificationsForAllSubtitles(
            subtitleElements, "x", currentSubElement.source.x),
          ...modificationsForAllSubtitles(
            subtitleElements, "width", currentSubElement.source.width),
          ...modificationsForAllSubtitles(subtitleElements, "y", currentSubElement.source.y),
          ...modificationsForAllSubtitles(subtitleElements, "height", currentSubElement.source.height)
        });
        isUpdating.current = false;
      };

      if (activeElement && isSubtitleText(activeElement.source.name) && !isUpdating.current) {
        updateSubtitles(activeElement);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      activeElement && activeElement.source && activeElement.source.x,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      activeElement && activeElement.source && activeElement.source.width,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      activeElement && activeElement.source && activeElement.source.y,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      activeElement && activeElement.source && activeElement.source.height,
      isUpdating.current
    ]
  );

  const handleLanguageChange = async (selectedSubtitle: Subtitle | AWSLanguage) => {
    const subtitleSelected = selectedSubtitle as Subtitle;
    const isSubtitleInstance = !!subtitleSelected.id;
    if (!isSubtitleInstance) return;

    // it does not need to change if it is already active
    if (subtitleSelected.active) return;

    if (!changingLanguage) {
      setChangingLanguage(true);
      // we want to copy the current properties to the new loaded subtitles
      const subtitleElement = (preview.state?.elements || []).find(
        element => isSubtitleText(element.source.name)
      );

      await removeSubtitlesElements(handleUpdateData, preview, subtitles);

      const index = subtitles.findIndex(subtitle => subtitle.id === subtitleSelected.id);
      const language = subtitles[index];
      if (language) {
        const elementId = await loadSubtitle(
          handleUpdateData,
          subtitles,
          subtitles[index],
          preview,
          elements,
          0,
          organization.name,
          burnSubtitles,
          subtitleElement,
          language.language
        );
        if (elementId) await preview.setActiveElements([elementId]);
      }
      setChangingLanguage(false);
    }
  };

  const handleSelectTranslation = (value: boolean) => {
    setShowLanguageOptions(value);
  };

  const handleShowStyles = () => {
    setShowStyles(true);
  };

  const handleBackStyles = () => {
    setShowStyles(false);
    setShowLanguageOptions(false);
  };

  return (
    <div className="Subtitles">
      <SubtitleHeader
        preview={preview}
        subtitlesOn={subtitlesOn}
        burnSubtitles={burnSubtitles}
        showStyles={showStyles}
        showAnimations={showAnimations}
        showLanguageOptions={showLanguageOptions}
        changingLanguage={changingLanguage}
        activeElement={activeElement}
        error={error}
        setShowAnimations={setShowAnimations}
        onBackStyles={handleBackStyles}
        onLanguageChange={handleLanguageChange}
        onSelectTranslation={handleSelectTranslation}
        onShowStyles={handleShowStyles}
      />

      {showAnimations
        ? (
          <AnimationScreen
            context={Context.Subtitle}
            preview={preview}
            activeElement={activeElement}
          />
        ) : (
          <SubtitlesContainer
            subtitlesOn={subtitlesOn}
            activeLanguage={activeLanguage}
            changingLanguage={changingLanguage}
            onLanguageChange={handleLanguageChange}
            preview={preview}
            onSelectTranslation={handleSelectTranslation}
            onSetError={setError}
            showStyles={showStyles}
            showLanguageOptions={showLanguageOptions}
            subtitleElements={subtitleElements}
          />
        )}

    </div>
  );
};

export default Subtitles;
